/* eslint-disable react/require-default-props */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import styled, { keyframes, css } from 'styled-components';

import Modal from '../../../../components/Modal/Modal';
import LocationPickModal from '../../../../components/LocationPickModal';
import {
   createAddress,
   updateAddress,
} from '../../../../services/customers/customersApi';
import {
   lookupCep,
   CepNotFoundError,
} from '../../../../services/viacep/viacepApi';
import { formatCep } from '../../utils/format';
import { primaryColor } from '../../../../config/colors';
import { FormGrid } from '../../../../styles/layout/FormGrid';
import { Field, RequiredMark } from '../../../../styles/forms/Field';

// Duração do highlight de sucesso (ms) — curto, só p/ chamar atenção do operador
// de que o endereço foi preenchido.
const CEP_HIGHLIGHT_MS = 1200;

// Pulso verde curto em volta do input quando a busca do CEP retorna com sucesso.
const cepSuccessPulse = keyframes`
   0%   { box-shadow: 0 0 0 0 rgba(46, 160, 67, 0.55); border-color: #2ea043; }
   100% { box-shadow: 0 0 0 6px rgba(46, 160, 67, 0); border-color: #2ea043; }
`;

// Field com highlight de sucesso quando $highlight — usado no CEP e em todos os
// campos preenchidos pelo autofill (logradouro/bairro/cidade/UF/complemento).
const HighlightField = styled(Field)`
   ${({ $highlight }) =>
      $highlight
         ? css`
              & input:not([type='checkbox']),
              & select {
                 animation: ${cepSuccessPulse} ${CEP_HIGHLIGHT_MS}ms ease-out;
                 border-color: #2ea043;
              }
           `
         : ''}
`;

const CepInputRow = styled.div`
   display: flex;
   gap: 8px;
   align-items: stretch;

   & > input {
      flex: 1 1 auto;
      min-width: 0;
   }
`;

const CepButton = styled.button`
   flex: 0 0 auto;
   white-space: nowrap;
   padding: 0 14px;
   min-height: 40px;
   border-radius: 10px;
   border: 1px solid ${primaryColor};
   background: ${primaryColor};
   color: #fff;
   font-size: 0.75rem;
   font-weight: 700;
   cursor: pointer;

   &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
   }
   &:hover:not(:disabled) {
      filter: brightness(0.95);
   }
`;

// Botão do seletor de ponto (outline) — diferencia da ação primária de salvar.
const PointButton = styled.button`
   display: inline-flex;
   align-items: center;
   gap: 6px;
   padding: 0 14px;
   min-height: 40px;
   border-radius: 10px;
   border: 1px solid ${primaryColor};
   background: transparent;
   color: ${primaryColor};
   font-size: 0.78rem;
   font-weight: 700;
   cursor: pointer;

   &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
   }
   &:hover:not(:disabled) {
      background: rgba(0, 0, 0, 0.03);
   }
`;

const COORD_TEXT_STYLE = {
   fontSize: 12.5,
   fontWeight: 600,
   fontVariantNumeric: 'tabular-nums',
};

// 27 unidades federativas (26 estados + DF).
const BRAZIL_STATES = [
   'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS',
   'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC',
   'SP', 'SE', 'TO',
];

const FIELD_ERROR_STYLE = {
   fontSize: 11,
   color: '#b71c1c',
   marginTop: 2,
};

const CEP_HINT_STYLE = {
   fontSize: 11,
   color: '#6b7280',
   marginTop: 2,
};

function getInitialForm(address) {
   return {
      label: address?.label ?? '',
      street: address?.street ?? '',
      number: address?.number ?? '',
      complement: address?.complement ?? '',
      neighborhood: address?.neighborhood ?? '',
      city: address?.city ?? '',
      state: address?.state ?? '',
      postal_code: address?.postal_code ?? '',
      is_primary: address?.is_primary ?? false,
      // Geolocalização (pin manual). Coordenada + âncora place_id; enviadas só
      // quando o operador fixa o ponto no mapa.
      latitude: address?.latitude ?? null,
      longitude: address?.longitude ?? null,
      place_id: address?.place_id ?? null,
   };
}

export default function AddressFormModal({
   isOpen,
   onClose,
   customerId,
   address = null,
   onSaved,
}) {
   const isEditMode = Boolean(address?.id);

   const [form, setForm] = useState(() => getInitialForm(address));
   const [fieldErrors, setFieldErrors] = useState({});
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [isCepLoading, setIsCepLoading] = useState(false);
   const [isMapOpen, setIsMapOpen] = useState(false);
   // Campos que acabaram de ser preenchidos pelo autofill (highlight de sucesso).
   const [highlightedFields, setHighlightedFields] = useState([]);

   // Último CEP consultado no ViaCEP — evita rebater a mesma consulta quando o
   // operador entra/sai do campo sem alterar o valor.
   const lastCepLookupRef = useRef(null);
   // Timer do highlight — limpo ao reabrir/desmontar p/ não vazar setState.
   const highlightTimerRef = useRef(null);

   // Re-sincroniza o form sempre que o modal abre (novo ou editando outro).
   useEffect(() => {
      if (isOpen) {
         setForm(getInitialForm(address));
         setFieldErrors({});
         setIsSubmitting(false);
         setIsCepLoading(false);
         setHighlightedFields([]);
         if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
         // Não redispara autofill ao reabrir/editar: marca o CEP atual como já
         // "resolvido" para que só uma edição real do operador dispare a busca.
         lastCepLookupRef.current = (address?.postal_code ?? '').replace(
            /\D/g,
            '',
         );
      }
   }, [isOpen, address]);

   // Limpa o timer do highlight ao desmontar.
   useEffect(
      () => () => {
         if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
      },
      [],
   );

   const updateField = (field, value) => {
      setForm((prev) => ({ ...prev, [field]: value }));
      setFieldErrors((prev) => {
         if (!prev[field]) return prev;
         const next = { ...prev };
         delete next[field];
         return next;
      });
   };

   // Dispara o highlight verde de sucesso nos campos preenchidos por uma janela
   // curta. Reinicia a animação mesmo em buscas consecutivas (off → on no
   // próximo frame).
   function triggerCepHighlight(fields) {
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
      setHighlightedFields([]);
      requestAnimationFrame(() => {
         setHighlightedFields(fields);
         highlightTimerRef.current = setTimeout(
            () => setHighlightedFields([]),
            CEP_HIGHLIGHT_MS,
         );
      });
   }

   // Autofill via ViaCEP. Sobrescreve os campos derivados do CEP (logradouro/
   // bairro/cidade/UF); complemento é preenchimento FRACO (só se estiver vazio);
   // número e coordenada nunca são tocados. Erro nunca é silencioso.
   //
   // `explicit` = clique no botão "Pesquisar CEP": rebusca mesmo se o CEP não
   // mudou e avisa quando está incompleto. No blur, fica silencioso e não repete
   // a mesma consulta.
   async function runCepLookup({ explicit } = {}) {
      const digits = (form.postal_code ?? '').replace(/\D/g, '');
      if (digits.length !== 8) {
         if (explicit) {
            toast.error('Informe um CEP com 8 dígitos para pesquisar.');
         }
         return;
      }
      if (!explicit && digits === lastCepLookupRef.current) return;

      lastCepLookupRef.current = digits;
      setIsCepLoading(true);
      try {
         const result = await lookupCep(digits);
         if (!result) return;

         // Complemento é preenchimento FRACO: só entra se o operador não digitou.
         const fillsComplement =
            !form.complement.trim() && Boolean(result.complement);

         setForm((prev) => ({
            ...prev,
            postal_code: formatCep(result.postal_code) || prev.postal_code,
            street: result.street || prev.street,
            neighborhood: result.neighborhood || prev.neighborhood,
            city: result.city || prev.city,
            state: result.state || prev.state,
            complement: fillsComplement ? result.complement : prev.complement,
         }));

         // Campos efetivamente preenchidos → recebem highlight e têm erro limpo.
         const filled = ['postal_code'];
         if (result.street) filled.push('street');
         if (result.neighborhood) filled.push('neighborhood');
         if (result.city) filled.push('city');
         if (result.state) filled.push('state');
         if (fillsComplement) filled.push('complement');

         setFieldErrors((prev) => {
            const next = { ...prev };
            filled.forEach((field) => delete next[field]);
            return next;
         });
         triggerCepHighlight(filled);
      } catch (err) {
         if (err instanceof CepNotFoundError) {
            toast.error('CEP não encontrado — preencha manualmente.');
         } else {
            toast.error(
               'Não foi possível consultar o CEP. Preencha manualmente.',
            );
         }
      } finally {
         setIsCepLoading(false);
      }
   }

   async function handleSubmit() {
      if (isSubmitting) return;

      if (!form.street.trim()) {
         setFieldErrors({ street: 'Logradouro é obrigatório.' });
         toast.error('Verifique os campos destacados.');
         return;
      }

      const payload = {
         label: form.label.trim() || null,
         street: form.street.trim(),
         number: form.number.trim() || null,
         complement: form.complement.trim() || null,
         neighborhood: form.neighborhood.trim() || null,
         city: form.city.trim() || null,
         state: form.state || null,
         postal_code: form.postal_code.trim() || null,
         is_primary: form.is_primary,
      };

      // Geolocalização: enviada como PAR (o backend valida par obrigatório e,
      // com o pin, marca geocode_status='ok'). place_id acompanha quando veio de
      // um geocode; num pin manual puro vai null (zera a âncora antiga).
      if (form.latitude != null && form.longitude != null) {
         payload.latitude = Number(form.latitude);
         payload.longitude = Number(form.longitude);
         payload.place_id = form.place_id || null;
      }

      setIsSubmitting(true);
      setFieldErrors({});

      try {
         if (isEditMode) {
            const { message } = await updateAddress(
               customerId,
               address.id,
               payload,
            );
            toast.success(message);
         } else {
            const { message } = await createAddress(customerId, payload);
            toast.success(message);
         }
         onSaved?.();
         onClose?.();
      } catch (err) {
         const responseErrors = err?.response?.data?.errors;
         if (Array.isArray(responseErrors) && responseErrors.length > 0) {
            const byField = {};
            responseErrors.forEach((entry) => {
               if (entry?.field) byField[entry.field] = entry.message;
            });
            setFieldErrors(byField);
            toast.error(
               err?.response?.data?.message ?? 'Verifique os campos destacados.',
            );
         } else {
            toast.error(
               err?.response?.data?.message ??
                  err?.message ??
                  'Erro ao salvar endereço.',
            );
         }
      } finally {
         setIsSubmitting(false);
      }
   }

   const hasCoord = form.latitude != null && form.longitude != null;
   // Endereço textual passado ao picker para pré-geocodificar/centrar o mapa.
   const pickerAddress = [
      [form.street, form.number]
         .map((s) => (s || '').trim())
         .filter(Boolean)
         .join(', '),
      (form.neighborhood || '').trim(),
      [form.city, form.state]
         .map((s) => (s || '').trim())
         .filter(Boolean)
         .join(' - '),
      (form.postal_code || '').trim(),
   ]
      .filter(Boolean)
      .join(', ');

   const footer = (
      <>
         <button type="button" onClick={onClose} disabled={isSubmitting}>
            Cancelar
         </button>
         <button type="button" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Salvando...' : 'Salvar endereço'}
         </button>
      </>
   );

   return (
      <Modal
         isOpen={isOpen}
         onClose={onClose}
         title={isEditMode ? 'Editar endereço' : 'Adicionar endereço'}
         maxWidth="640px"
         footer={footer}
      >
         <FormGrid>
            <Field className="col-6">
               <label htmlFor="address-label">Identificação</label>
               <input
                  id="address-label"
                  type="text"
                  maxLength={40}
                  placeholder="Ex: Comercial, Residencial..."
                  value={form.label}
                  onChange={(e) => updateField('label', e.target.value)}
                  disabled={isSubmitting}
               />
            </Field>

            <HighlightField
               className="col-6"
               $highlight={highlightedFields.includes('postal_code')}
            >
               <label htmlFor="address-postal-code">CEP</label>
               <CepInputRow>
                  <input
                     id="address-postal-code"
                     type="text"
                     inputMode="numeric"
                     placeholder="00000-000"
                     value={form.postal_code}
                     onChange={(e) =>
                        updateField('postal_code', formatCep(e.target.value))
                     }
                     onBlur={() => runCepLookup()}
                     disabled={isSubmitting || isCepLoading}
                     aria-invalid={!!fieldErrors.postal_code || undefined}
                  />
                  <CepButton
                     type="button"
                     onClick={() => runCepLookup({ explicit: true })}
                     disabled={isSubmitting || isCepLoading}
                  >
                     {isCepLoading ? 'Buscando…' : 'Pesquisar CEP'}
                  </CepButton>
               </CepInputRow>
               {isCepLoading && (
                  <span style={CEP_HINT_STYLE}>Buscando endereço…</span>
               )}
               {fieldErrors.postal_code && (
                  <span style={FIELD_ERROR_STYLE}>
                     {fieldErrors.postal_code}
                  </span>
               )}
            </HighlightField>

            <HighlightField
               className="col-8"
               $highlight={highlightedFields.includes('street')}
            >
               <label htmlFor="address-street">
                  Logradouro
                  <RequiredMark>*</RequiredMark>
               </label>
               <input
                  id="address-street"
                  type="text"
                  maxLength={255}
                  value={form.street}
                  onChange={(e) => updateField('street', e.target.value)}
                  disabled={isSubmitting}
                  aria-invalid={!!fieldErrors.street || undefined}
               />
               {fieldErrors.street && (
                  <span style={FIELD_ERROR_STYLE}>{fieldErrors.street}</span>
               )}
            </HighlightField>

            <Field className="col-4">
               <label htmlFor="address-number">Número</label>
               <input
                  id="address-number"
                  type="text"
                  maxLength={20}
                  value={form.number}
                  onChange={(e) => updateField('number', e.target.value)}
                  disabled={isSubmitting}
               />
            </Field>

            <HighlightField
               className="col-6"
               $highlight={highlightedFields.includes('complement')}
            >
               <label htmlFor="address-complement">Complemento</label>
               <input
                  id="address-complement"
                  type="text"
                  maxLength={255}
                  value={form.complement}
                  onChange={(e) => updateField('complement', e.target.value)}
                  disabled={isSubmitting}
               />
            </HighlightField>

            <HighlightField
               className="col-6"
               $highlight={highlightedFields.includes('neighborhood')}
            >
               <label htmlFor="address-neighborhood">Bairro</label>
               <input
                  id="address-neighborhood"
                  type="text"
                  maxLength={120}
                  value={form.neighborhood}
                  onChange={(e) => updateField('neighborhood', e.target.value)}
                  disabled={isSubmitting}
               />
            </HighlightField>

            <HighlightField
               className="col-8"
               $highlight={highlightedFields.includes('city')}
            >
               <label htmlFor="address-city">Cidade</label>
               <input
                  id="address-city"
                  type="text"
                  maxLength={120}
                  value={form.city}
                  onChange={(e) => updateField('city', e.target.value)}
                  disabled={isSubmitting}
               />
            </HighlightField>

            <HighlightField
               className="col-4"
               $highlight={highlightedFields.includes('state')}
            >
               <label htmlFor="address-state">UF</label>
               <select
                  id="address-state"
                  value={form.state}
                  onChange={(e) => updateField('state', e.target.value)}
                  disabled={isSubmitting}
               >
                  <option value="">—</option>
                  {BRAZIL_STATES.map((uf) => (
                     <option key={uf} value={uf}>
                        {uf}
                     </option>
                  ))}
               </select>
            </HighlightField>

            <Field className="col-12">
               <label>Localização (ponto no mapa)</label>
               <div
                  style={{
                     display: 'flex',
                     alignItems: 'center',
                     gap: 10,
                     flexWrap: 'wrap',
                  }}
               >
                  <PointButton
                     type="button"
                     onClick={() => setIsMapOpen(true)}
                     disabled={isSubmitting}
                  >
                     {hasCoord
                        ? 'Ajustar ponto no mapa'
                        : 'Definir ponto no mapa'}
                  </PointButton>
                  {hasCoord && (
                     <span style={COORD_TEXT_STYLE}>
                        {Number(form.latitude).toFixed(5)},{' '}
                        {Number(form.longitude).toFixed(5)}
                     </span>
                  )}
               </div>
               <span style={CEP_HINT_STYLE}>
                  {hasCoord
                     ? 'Ponto definido — salvo no endereço e reutilizado no Comercial e na roteirização.'
                     : 'Opcional. Fixe o ponto para o endereço já entrar roteirizável, sem ajuste no board.'}
               </span>
            </Field>

            <Field className="col-12">
               <label
                  htmlFor="address-is-primary"
                  style={{
                     flexDirection: 'row',
                     alignItems: 'center',
                     gap: 8,
                     display: 'flex',
                  }}
               >
                  <input
                     id="address-is-primary"
                     type="checkbox"
                     checked={form.is_primary}
                     onChange={(e) =>
                        updateField('is_primary', e.target.checked)
                     }
                     disabled={isSubmitting}
                  />
                  Endereço principal
               </label>
            </Field>
         </FormGrid>

         {isMapOpen && (
            <LocationPickModal
               isOpen
               onClose={() => setIsMapOpen(false)}
               initialAddress={pickerAddress}
               initialCoord={
                  hasCoord
                     ? {
                          lat: Number(form.latitude),
                          lng: Number(form.longitude),
                       }
                     : null
               }
               onConfirm={(point) =>
                  setForm((prev) => ({
                     ...prev,
                     latitude: point.latitude,
                     longitude: point.longitude,
                     place_id: point.place_id ?? null,
                  }))
               }
            />
         )}
      </Modal>
   );
}

AddressFormModal.propTypes = {
   isOpen: PropTypes.bool.isRequired,
   onClose: PropTypes.func.isRequired,
   customerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      .isRequired,
   // eslint-disable-next-line react/forbid-prop-types
   address: PropTypes.object,
   onSaved: PropTypes.func,
};
